/**
 * Endpoints de **Finanzas → Pagos de Operadores**
 * (`/api/finanzas/pagos-operadores`).
 *
 * El listado trae, junto a las filas paginadas, los cuatro contadores del filtro y
 * los catálogos (operadores con su tasa, cuentas, métodos de pago y el tope de
 * lote), así que la pantalla no necesita pedirlos aparte. La verificación
 * —individual y en lote— es `multipart`, porque el comprobante viaja en el mismo
 * formulario.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CuerpoLote,
  CuerpoPago,
  EstadoPagos,
  FiltrosPagos,
  ListadoPagos,
  MovimientoPagoRespuesta,
  PagoRespuesta,
  PendientesRespuesta,
} from './pagos-operadores.types';

export const pagosKeys = {
  todo: ['finanzas', 'pagos-operadores'] as const,
  lista: (filtros: FiltrosPagos) => ['finanzas', 'pagos-operadores', 'lista', filtros] as const,
  venta: (tipo: string, ventaId: number) =>
    ['finanzas', 'pagos-operadores', 'venta', tipo, ventaId] as const,
  movimiento: (id: number) => ['finanzas', 'pagos-operadores', 'movimiento', id] as const,
};

/** Cadena de consulta del listado. */
export function cadenaDePagos(filtros: FiltrosPagos): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.estado && filtros.estado !== 'all') parametros.set('estado', filtros.estado);
  if (filtros.operador) parametros.set('operador', String(filtros.operador));
  if (filtros.buscar) parametros.set('buscar', filtros.buscar);
  if (filtros.page > 1) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

/** Formulario de la verificación individual. */
function formularioDe(datos: CuerpoPago): FormData {
  const formulario = new FormData();

  formulario.set('tipo', datos.tipo);
  formulario.set('venta_id', datos.venta_id);
  formulario.set('operador_id', datos.operador_id);
  formulario.set('cuenta_id', datos.cuenta_id);
  formulario.set('metodo_pago_id', datos.metodo_pago_id);
  formulario.set('fecha', datos.fecha);
  formulario.set('referencia', datos.referencia);
  formulario.set('notas', datos.notas);

  if (datos.comprobante) formulario.set('voucher_file', datos.comprobante);

  return formulario;
}

/** Formulario de la verificación en lote. */
function formularioDeLote(datos: CuerpoLote): FormData {
  const formulario = new FormData();

  formulario.set('pagos', JSON.stringify(datos.pagos));
  formulario.set('operador_id', datos.operador_id);
  formulario.set('cuenta_id', datos.cuenta_id);
  formulario.set('metodo_pago_id', datos.metodo_pago_id);
  formulario.set('fecha', datos.fecha);
  formulario.set('monto_real', datos.monto_real);
  formulario.set('referencia', datos.referencia);
  formulario.set('notas', datos.notas);

  if (datos.comprobante) formulario.set('voucher_file', datos.comprobante);

  return formulario;
}

export const pagosApi = {
  listar: (filtros: FiltrosPagos, signal?: AbortSignal) =>
    apiClient.get<ListadoPagos>(`/api/finanzas/pagos-operadores${cadenaDePagos(filtros)}`, {
      signal,
    }),

  pendientes: (filtros: FiltrosPagos, signal?: AbortSignal) =>
    apiClient.get<PendientesRespuesta>(
      `/api/finanzas/pagos-operadores/pendientes${cadenaDePagos({ ...filtros, estado: 'pending', page: 1 })}`,
      { signal },
    ),

  venta: (tipo: string, ventaId: number, signal?: AbortSignal) =>
    apiClient.get<PagoRespuesta>(`/api/finanzas/pagos-operadores/venta/${tipo}/${ventaId}`, {
      signal,
    }),

  verificar: (datos: CuerpoPago) =>
    apiClient.post<{ movement_id: number; comision: number; neto: number; message: string }>(
      '/api/finanzas/pagos-operadores/verificar',
      { body: formularioDe(datos) },
    ),

  verificarLote: (datos: CuerpoLote) =>
    apiClient.post<{
      movement_id: number;
      pagos: number;
      bruto: number;
      comision: number;
      neto_esperado: number;
      monto_real: number;
      diferencia: number;
      message: string;
    }>('/api/finanzas/pagos-operadores/verificar-lote', { body: formularioDeLote(datos) }),

  movimiento: (id: number, signal?: AbortSignal) =>
    apiClient.get<MovimientoPagoRespuesta>(`/api/finanzas/pagos-operadores/movimiento/${id}`, {
      signal,
    }),

  anularMovimiento: (id: number) =>
    apiClient.delete<{ id: number; pagos: number; message: string }>(
      `/api/finanzas/pagos-operadores/movimiento/${id}`,
    ),

  urlComprobante: (id: number) => `/api/finanzas/pagos-operadores/movimiento/${id}/comprobante`,
};

/** Listado paginado con sus totales y catálogos. */
export function usePagosOperadores(filtros: FiltrosPagos) {
  return useQuery({
    queryKey: pagosKeys.lista(filtros),
    queryFn: ({ signal }) => pagosApi.listar(filtros, signal),
    placeholderData: (anterior) => anterior,
  });
}

/** Los pagos pendientes del filtro, para la selección masiva. */
export function usePagosPendientes(filtros: FiltrosPagos, activo: boolean) {
  return useQuery({
    queryKey: [...pagosKeys.lista(filtros), 'pendientes'],
    queryFn: ({ signal }) => pagosApi.pendientes(filtros, signal),
    enabled: activo,
  });
}

/** El pago que se va a verificar o a consultar. */
export function usePagoOperador(tipo: string | null, ventaId: number | null) {
  return useQuery({
    queryKey: pagosKeys.venta(tipo ?? '', ventaId ?? 0),
    queryFn: ({ signal }) => pagosApi.venta(tipo as string, ventaId as number, signal),
    enabled: Boolean(tipo) && Number(ventaId) > 0,
  });
}

/** El movimiento bancario de una verificación. */
export function useMovimientoPago(id: number | null) {
  return useQuery({
    queryKey: pagosKeys.movimiento(id ?? 0),
    queryFn: ({ signal }) => pagosApi.movimiento(id as number, signal),
    enabled: Number(id) > 0,
  });
}

/** Verificar un pago (crea el movimiento y marca la venta). */
export function useVerificarPago() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: CuerpoPago) => pagosApi.verificar(datos),
    onSuccess: () => cliente.invalidateQueries({ queryKey: pagosKeys.todo }),
  });
}

/** Verificar un lote de pagos con un solo movimiento. */
export function useVerificarLote() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: CuerpoLote) => pagosApi.verificarLote(datos),
    onSuccess: () => cliente.invalidateQueries({ queryKey: pagosKeys.todo }),
  });
}

/** Anular la verificación: libera los pagos que la componían. */
export function useAnularMovimientoPago() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => pagosApi.anularMovimiento(id),
    onSuccess: () => cliente.invalidateQueries({ queryKey: pagosKeys.todo }),
  });
}

/** Estados del filtro, como el selector del panel anterior. */
export const ESTADOS_PAGOS: { valor: EstadoPagos; etiqueta: string }[] = [
  { valor: 'all', etiqueta: 'Todos' },
  { valor: 'pending', etiqueta: 'Pendientes' },
  { valor: 'verified', etiqueta: 'Verificados' },
];
